// @ts-check
import { useCustomRef } from '@erpagroapp/common';
import { params } from './storeParams';

/** @import { Ref } from 'vue' */
/** @import { ReturnTypeToValueType } from './types' */
/** @import { Parameters, ParameterKeys } from './storeParams' */

/**
 * @typedef {{
 *  [K in ParameterKeys]: Ref<ReturnTypeToValueType<Parameters[K]['returnType']>>
 * }} ParamValues
 * */

/**
 * @internal
 */
export const paramValues = getInitialValues();

/**
 * @internal
 * @returns {boolean}
 */
export function hasValuesInLocalStorage() {
  const configurationParamsValue = localStorage.getItem('configurationParams');
  return configurationParamsValue !== null
    && configurationParamsValue !== '{}';
}

/**
 * @returns {ParamValues}
 */
function getInitialValues() {
  const initialValues = !hasValuesInLocalStorage()
    ? createValues()
    : mergeValuesWithConfigs(getValuesFromLocalStorage());

  return initialValues;
}

/**
 * @returns {ParamValues}
 */
function createValues() {
  const parameterValuesTemp = /** @type {Record<ParameterKeys, any>} */ ({});

  for (const [paramName, param] of Object.entries(params)) {
    const key = /** @type {ParameterKeys} */ (paramName);
    parameterValuesTemp[key] = useCustomRef(param.defaultValue, saveValuesToLocalStorage);
  }

  return parameterValuesTemp;
}

/**
 * @returns {ParamValues}
 */
function getValuesFromLocalStorage() {
  const storedParams = localStorage.getItem('configurationParams');
  let tempParams = /** @type {Record<ParameterKeys, any>} */ ({});

  if (storedParams) {
    tempParams = /** @type {ParamValues} */ (JSON.parse(storedParams));
  }

  const reactiveParams = /** @type {Record<ParameterKeys, any>} */ ({});
  for (const [name, param] of Object.entries(tempParams)) {
    const key = /** @type {ParameterKeys} */ (name);
    reactiveParams[key] = useCustomRef(param, saveValuesToLocalStorage);
  }

  return reactiveParams;
}

/**
 * @param {ParamValues} [values] - Values to be saved.
 * @returns {void}
 */
export function saveValuesToLocalStorage(values) {
  const tempParamValues = /** @type {Record<ParameterKeys, any>} */ ({});
  const valuesToSave = values ?? paramValues;

  for (const [paramName, param] of Object.entries(valuesToSave)) {
    const key = /** @type {ParameterKeys} */ (paramName);
    tempParamValues[key] = param.value;
  }

  localStorage.setItem('configurationParams', JSON.stringify(tempParamValues));
}

/**
 * Merges existing settings from local storage with the current configuration settings.
 * @param {ParamValues} existingValues - The values retrieved from local storage.
 * @returns {ParamValues} The merged settings values.
 */
function mergeValuesWithConfigs(existingValues) {
  const mergedValues = /** @type {Record<ParameterKeys, any>} */ ({ ...existingValues });
  let isChanged = false;

  for (const [paramName, param] of Object.entries(params)) {
    if (!Object.hasOwn(mergedValues, paramName)) {
      isChanged = true;
      const key = /** @type {ParameterKeys} */ (paramName);
      mergedValues[key] = useCustomRef(param.defaultValue, saveValuesToLocalStorage);
    }
  }

  if (isChanged) {
    saveValuesToLocalStorage(mergedValues);
  }

  return mergedValues;
}
